import type { ReactNode } from 'react';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useResolvedTarifDeckung } from '@eis/core';
import { assetOfErn } from '../../../model/deckung-overview';

/**
 * Which "Tarif & Deckung" a child tarif version pins, and its variant keys — the group pin picker's
 * read-only hint (SDD-018 §5): pinning a child version is also choosing the variants a group can
 * allow. Read through the service's resolution, so the console never re-derives the pin.
 */
export function ChildDeckungLine({
  tarifVersionErn,
}: {
  readonly tarifVersionErn: string;
}): ReactNode {
  const { t } = useTranslation('insurance');
  const resolved = useResolvedTarifDeckung({ tarifVersionErn });
  if (tarifVersionErn === '' || resolved.data === undefined) return null;

  const asset = assetOfErn(resolved.data.tarifAssetErn ?? '');
  const raw = resolved.data.config?.tariffVariations;
  const keys = Array.isArray(raw)
    ? raw.map((variation: unknown) =>
        typeof variation === 'object' && variation !== null && 'key' in variation
          ? String(variation.key)
          : '',
      )
    : [];

  return (
    <Typography variant="caption" color="text.secondary" data-testid="child-deckung-pin">
      {asset === undefined
        ? t('deckung.pinned.none')
        : t('deckung.pinned.childLine', {
            asset: asset.name,
            keys: keys.filter((key) => key !== '').join(', ') || t('deckung.common.dash'),
          })}
    </Typography>
  );
}
